import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Box } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ScrambleText from '../effects/ScrambleText';
import { FourK, TrueFull3D, PietImaging, EggModule } from '../icons/XionIcons';
import { useMotion } from '../../context/MotionContext';

gsap.registerPlugin(ScrollTrigger);

// Mask the dimming veil to the tower silhouette so it never paints a dark
// rectangle behind/around the tower (kept in JS so webpack doesn't try to
// resolve the public asset URL at build time).
const TOWER_MASK_STYLE = {
  WebkitMaskImage: "url('/assets/hero-tower-static.png')",
  maskImage: "url('/assets/hero-tower-static.png')",
  WebkitMaskSize: 'contain',
  maskSize: 'contain',
  WebkitMaskPosition: 'center',
  maskPosition: 'center',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
};

// Subtle layered grounding shadow for the spotlighted tower
const TOWER_SHADOW = {
  filter: 'drop-shadow(0 28px 50px rgba(0,0,0,0.6)) drop-shadow(0 10px 22px rgba(0,0,0,0.42))',
};

// Bespoke animated capability icons — contrasting colours, perpetual motion
const CAPABILITIES = [
  { Icon: FourK, label: '4K / UHD', color: '#00FFD1' },
  { Icon: TrueFull3D, label: 'True Full 3D', color: '#F08A24', highlight: true },
  { Icon: PietImaging, label: 'PIET Imaging', color: '#00B4D8' },
  { Icon: EggModule, label: 'EGG Module', color: '#9D7BFF' },
];

// Scroll stages drive the copy, the component spotlights and the moving light.
// Every callout with a `link` is clickable and routes to the matching product / system page.
const STAGES = [
  {
    id: 1,
    tag: 'Visualization',
    title: 'HD & 4K Imaging',
    subtitle: 'Crystal-Clear 4K & True Full 3D',
    description: 'Beyond 4K — XION is the only endoscopy tower delivering true Full 3D stroboscopic visualization for unparalleled depth, clarity and diagnostic precision.',
    cta: { text: 'Explore Monitors', link: '/products/medical-grade-monitors' },
    // spotlight target on the static tower (% of the tower image box)
    spot: { x: 59, y: 16, r: 34 },
    callouts: [
      { x: 72, y: 9, label: '4K / UHD Monitor', desc: 'Ultra-high definition', color: '#00FFD1', link: '/products/medical-grade-monitors' },
      { x: 38, y: 24, label: 'True Full 3D', desc: 'Stereoscopic depth — unique to XION', color: '#F08A24', highlight: true, link: '/system-solutions/endosurgery-3d-spectar' },
    ],
  },
  {
    id: 2,
    tag: 'Camera Platform',
    title: 'Spectar Processor',
    subtitle: 'One Platform · 2D & 3D up to 4K',
    description: 'The universal Spectar Connector links freely interchangeable camera heads and flexible endoscopes — switching between 2D, 3D and 4K on a single processor, enhanced by PIET imaging.',
    cta: { text: 'View Camera Systems', link: '/products/camera-processors-for-surgery' },
    spot: { x: 53, y: 40, r: 16 },
    callouts: [
      { x: 56, y: 33, label: 'Spectar Connector', desc: 'Universal · hot-swappable', color: '#00FFD1', link: '/products/camera-processors-for-surgery' },
      { x: 57, y: 47, label: 'PIET Technology', desc: 'lumino · chromo · spectro', color: '#00B4D8', highlight: true, link: '/technology/piet' },
    ],
  },
  {
    id: 3,
    tag: 'Phoniatrics',
    title: '3D Stroboscopy',
    subtitle: 'World-First · Noiseless & Flicker-Free',
    description: 'Integrated into the tower: XION\u2019s world-first 3D stroboscopy in 4K, HD and 3D — with real-time electroglottography (EGG) superimposed on the live stroboscopic image.',
    cta: { text: 'Explore EndoSTROBE', link: '/system-solutions/endostrobe-spectar' },
    spot: { x: 52, y: 48, r: 20 },
    callouts: [
      { x: 54, y: 42, label: '3D Stroboscopy', desc: 'World-first · noiseless', color: '#9D7BFF', highlight: true, link: '/system-solutions/endostrobe-spectar' },
      { x: 36, y: 56, label: 'Real-time EGG', desc: 'Electroglottography overlay', color: '#00B4D8', link: '/system-solutions/endostrobe-spectar' },
    ],
  },
  {
    id: 4,
    tag: 'Endoscopes',
    title: 'Scopes & Endoscopes',
    subtitle: 'Flexible & Rigid · Shared Spectar Connector',
    description: 'Spectar and Veezar flexible video nasopharyngoscopes plus a full range of rigid sinuscopes, laryngoscopes and 3D endoscopes — all hot-swappable on the same platform.',
    cta: { text: 'Browse Endoscopes', link: '/products/flexible-video-endoscopes' },
    spot: { x: 34, y: 60, r: 19 },
    callouts: [
      { x: 33, y: 55, label: 'Flexible Video Scopes', desc: 'Spectar & Veezar nasopharyngoscopes', color: '#00FFD1', highlight: true, link: '/products/flexible-video-endoscopes' },
      { x: 50, y: 71, label: 'Rigid Endoscopes', desc: 'Sinuscopes · laryngoscopes · 3D', color: '#F08A24', link: '/products/rigid-endoscopes' },
    ],
  },
  {
    id: 5,
    tag: 'Made in Berlin',
    title: 'Complete Workstation',
    subtitle: 'From Office Units to Full OR Towers',
    description: 'A harmonised stack — processor, MATRIX LED illumination, DiVAS documentation and 4K monitors — engineered in Berlin since 1991. In the Philippines, exclusively distributed by FMSInc.',
    cta: { text: 'Explore the Tower', link: '/system-solutions/endoscopy-tower' },
    spot: { x: 55, y: 50, r: 62 },
    callouts: [
      { x: 53, y: 50, label: 'MATRIX LED duo', desc: 'Almost-silent illumination', color: '#00FFD1', link: '/products/led-illumination-systems' },
      { x: 36, y: 64, label: 'DiVAS Software', desc: 'DICOM · HL7 · GDT', color: '#9D7BFF', link: '/products/software' },
      { x: 64, y: 85, label: 'FMSInc.', desc: 'Philippine Distributor', color: '#F08A24', link: '/contact-ph' },
    ],
  },
];

// Per-stage accent colours for the ambient background glow
const STAGE_ACCENTS = ['#00FFD1', '#00B4D8', '#9D7BFF', '#00FFD1', '#F08A24'];

/* Animated HUD connector lines + labels for the active stage */
const CalloutLayer = ({ callouts, origin = { x: 50, y: 50 } }) => (
  <>
    <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 15 }}>
      {callouts.map((c, i) => (
        <line
          key={i}
          className="hud-line"
          x1={`${origin.x}%`}
          y1={`${origin.y}%`}
          x2={`${c.x}%`}
          y2={`${c.y}%`}
          stroke={c.color}
          strokeWidth="1"
          style={{ animationDelay: `${0.15 + i * 0.15}s` }}
        />
      ))}
    </svg>
    {callouts.map((c, i) => {
      const slug = c.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const labelBody = (
        <>
          <p className="text-xs font-mono font-bold flex items-center gap-1.5" style={{ color: c.color }}>
            {c.highlight && <Box size={12} className="cap-spin" />}
            {c.label}
            {c.link && <ArrowRight size={11} className="opacity-70" />}
          </p>
          <p className="text-[11px] text-white/60">{c.desc}</p>
        </>
      );
      const labelClass = `absolute left-6 top-0 -translate-y-1/2 whitespace-nowrap bg-black/85 backdrop-blur-sm px-3 py-2 border transition-all duration-200 ${c.link ? 'cursor-pointer hover:bg-black hover:scale-[1.04] hover:shadow-[0_6px_24px_-6px_rgba(0,0,0,0.7)]' : ''}`;
      const labelStyle = { borderColor: c.highlight ? c.color : 'rgba(255,255,255,0.18)' };
      return (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ delay: 0.2 + i * 0.15, duration: 0.4 }}
          className="absolute z-20"
          style={{ left: `${c.x}%`, top: `${c.y}%`, transform: 'translate(-50%, -50%)' }}
          data-testid={`hero-callout-${slug}`}
        >
          <div
            className="w-3 h-3 rounded-full animate-pulse"
            style={{ background: c.color, boxShadow: `0 0 12px ${c.color}` }}
          />
          {c.link ? (
            <Link to={c.link} className={labelClass} style={labelStyle} data-testid={`hero-callout-link-${slug}`}>
              {labelBody}
            </Link>
          ) : (
            <div className={labelClass} style={labelStyle}>
              {labelBody}
            </div>
          )}
        </motion.div>
      );
    })}
  </>
);

const MultiPanelHero = () => {
  const sectionRef = useRef(null);
  const progressRef = useRef(null);
  const stageRef = useRef(0);
  const cardRefs = useRef([]);
  const [stageIndex, setStageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
  );
  const { prefersReducedMotion } = useMotion();

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || prefersReducedMotion || isMobile) return;

    const trigger = ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.5,
      onUpdate: (self) => {
        const p = self.progress;
        if (progressRef.current) {
          progressRef.current.style.transform = `scaleY(${p})`;
        }
        const idx = Math.min(STAGES.length - 1, Math.floor(p * STAGES.length));
        if (idx !== stageRef.current) {
          stageRef.current = idx;
          setStageIndex(idx);
        }
      },
    });
    ScrollTrigger.refresh();

    return () => { trigger.kill(); };
  }, [prefersReducedMotion, isMobile]);

  // Mobile: the spotlight follows whichever stage card is in the active scroll band
  useEffect(() => {
    if (!isMobile) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.stage);
            if (!Number.isNaN(idx) && idx !== stageRef.current) {
              stageRef.current = idx;
              setStageIndex(idx);
            }
          }
        });
      },
      { rootMargin: '-68% 0px -29% 0px', threshold: 0 }
    );
    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [isMobile]);

  const stage = STAGES[stageIndex];
  const accent = STAGE_ACCENTS[stageIndex] || '#00FFD1';
  const spot = stage.spot || { x: 55, y: 45, r: 40 };

  return (
    <section className="relative bg-black" data-testid="hero-section">
      {/* Intro */}
      <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-black via-black to-[#0a0a0a] overflow-hidden">
        {/* Subtle clinical backdrop — surgical team viewing in true 3D ("see more than others") */}
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: "url('/assets/see-more-bg.jpg')", opacity: 0.45 }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/95" aria-hidden />
        {/* stronger readability vignette so PHILIPPINES + headline stand out */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(52% 50% at 50% 44%, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.4) 48%, transparent 76%)' }} aria-hidden />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative text-center px-6 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col items-center"
          >
            <div className="font-mono text-[#00FFD1] text-xs md:text-sm tracking-[0.3em] uppercase mb-8" data-testid="hero-kicker">
              [ Precision Endoscopy · Engineered Since 1991 ]
            </div>

            <div className="flex flex-col items-center mb-8">
              <img
                src="/assets/xion-logo.png"
                alt="XION Medical"
                className="h-28 md:h-32 w-auto mb-3"
                style={{ filter: 'drop-shadow(0 0 30px rgba(0, 255, 209, 0.3)) drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5))' }}
              />
              <span className="text-xl md:text-2xl font-light tracking-[0.3em]" data-testid="ph-flag-text" aria-label="Philippines">
                {'PHILIPPINES'.split('').map((ch, i) => (
                  <span key={i} style={{ color: ['#0038A8', '#CE1126', '#FCD116', '#FFFFFF'][i % 4], textShadow: '0 0 8px rgba(0,0,0,0.6)' }}>{ch}</span>
                ))}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-8 flex items-center justify-center gap-3">
              <span className="hidden md:inline text-white/20 font-extralight text-5xl">[</span>
              <span className="text-[#00FFD1] text-glow">See more than others.</span>
              <span className="hidden md:inline text-white/20 font-extralight text-5xl">]</span>
            </h1>

            {/* Animated capability icons */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-8" data-testid="hero-capabilities">
              {CAPABILITIES.map(({ Icon, label, color, highlight }) => (
                <div
                  key={label}
                  data-testid={`capability-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                  className={`cap-chip ${highlight ? 'is-highlight' : ''} flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/[0.03] backdrop-blur-sm border`}
                  style={{ '--cap-color': color, '--cap-glow': `${color}66` }}
                >
                  <Icon size={18} style={{ color }} />
                  <span className="text-xs font-mono tracking-wider" style={{ color }}>{label}</span>
                </div>
              ))}
            </div>

            <p className="text-white/40 text-sm animate-pulse">↓ Scroll to explore the tower ↓</p>
          </motion.div>
        </div>
      </div>

      {/* Scroll-scrubbed Endoscopy Tower — desktop */}
      {!isMobile && (
      <div ref={sectionRef} className="relative bg-[#0c0c0c]" style={{ height: `${STAGES.length * 100}vh` }}>
        <div className="sticky top-0 h-screen overflow-hidden bg-[#0c0c0c]">
          {/* Ambient HUD background — no flat black void while scrubbing the tower */}
          <div className="absolute inset-0 grid-bg opacity-50" />
          <div
            className="absolute inset-0 transition-all duration-700 ease-out"
            style={{ background: `radial-gradient(58% 55% at 50% 48%, ${accent}26 0%, ${accent}0d 38%, transparent 72%)` }}
          />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(130% 120% at 50% -10%, transparent 55%, rgba(0,0,0,0.55) 100%)' }} />
          <div className="relative z-10 grid grid-cols-12 gap-4 h-full items-center">
            {/* Left — dynamic copy */}
            <div className="col-span-12 lg:col-span-3 flex items-center p-6 lg:p-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={stageIndex}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.4 }}
                  className="space-y-5"
                  data-testid="hero-stage-copy"
                >
                  <div className="terminal-label text-[#00FFD1] text-xs">// {stage.tag} · {stage.id}/{STAGES.length}</div>
                  <h2 className="text-3xl md:text-4xl font-bold">
                    <ScrambleText text={stage.title} className="text-[#00FFD1]" />
                  </h2>
                  <p className="text-lg text-white/80">{stage.subtitle}</p>
                  <p className="text-white/60 leading-relaxed text-sm">{stage.description}</p>
                  <Link to={stage.cta.link} className="btn-iridescent inline-flex" data-testid="hero-stage-cta">
                    {stage.cta.text}
                    <ArrowRight size={16} />
                  </Link>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Center — fixed tower with a scroll-driven spotlight */}
            <div className="col-span-12 lg:col-span-6 relative flex items-center justify-center h-full">
              <div
                className="tower-stage relative"
                style={{
                  height: 'min(78vh, 660px)',
                  aspectRatio: '957 / 1500',
                  '--sx': `${spot.x}%`,
                  '--sy': `${spot.y}%`,
                  '--sr': `${spot.r}%`,
                  '--acc': accent,
                }}
              >
                <img
                  src="/assets/hero-tower-static.png"
                  alt="XION Endoscopy Tower"
                  className="absolute inset-0 w-full h-full object-contain"
                  style={TOWER_SHADOW}
                  data-testid="hero-tower-static"
                />

                {/* accent glow pooling under the spotlight */}
                <div className="tower-glow" aria-hidden />
                {/* darkening veil with a bright spotlight hole */}
                <div className="tower-spotlight" style={TOWER_MASK_STYLE} aria-hidden />

                {/* travelling reticle at the spotlight centre */}
                <div
                  className="tower-reticle"
                  style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
                  aria-hidden
                >
                  <span className="tr-ring" style={{ borderColor: accent, boxShadow: `0 0 18px ${accent}55` }} />
                  <span className="tr-dot" style={{ background: accent, boxShadow: `0 0 14px ${accent}` }} />
                </div>

                <AnimatePresence mode="wait">
                  <motion.div key={stageIndex} className="absolute inset-0">
                    <CalloutLayer callouts={stage.callouts} origin={spot} />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Right — stage tracker + scrub progress */}
            <div className="col-span-12 lg:col-span-3 hidden lg:flex items-center justify-center">
              <div className="flex items-stretch gap-4">
                <div className="relative w-[2px] bg-white/10 rounded-full overflow-hidden self-stretch">
                  <div ref={progressRef} className="absolute top-0 left-0 w-full h-full bg-[#00FFD1] origin-top" style={{ transform: 'scaleY(0)' }} />
                </div>
                <div className="space-y-5 py-4">
                  {STAGES.map((s, idx) => (
                    <div key={s.id} className={`flex items-center gap-3 transition-opacity duration-300 ${idx === stageIndex ? 'opacity-100' : 'opacity-40'}`}>
                      <div className={`w-1 h-10 transition-colors ${idx === stageIndex ? 'bg-[#00FFD1]' : 'bg-white/20'}`} />
                      <div>
                        <p className="text-white text-sm font-semibold">{s.title}</p>
                        <p className="text-white/40 text-xs">{s.tag}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Mobile tower showcase — sticky spotlighted tower driven by the stage cards */}
      {isMobile && (
        <div className="relative bg-[#0c0c0c]" data-testid="hero-tower-mobile">
          {/* Sticky spotlighted tower — offset below the fixed banner + header so the full tower shows */}
          <div className="sticky top-[104px] z-20 h-[44vh] bg-[#0c0c0c] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 grid-bg opacity-40" />
            <div
              className="absolute inset-0 transition-all duration-700 ease-out"
              style={{ background: `radial-gradient(70% 60% at 50% 45%, ${accent}26 0%, ${accent}0d 40%, transparent 72%)` }}
            />
            <div
              className="tower-stage relative h-[38vh] max-h-[340px]"
              style={{
                aspectRatio: '957 / 1500',
                '--sx': `${spot.x}%`,
                '--sy': `${spot.y}%`,
                '--sr': `${spot.r}%`,
                '--acc': accent,
              }}
            >
              <img
                src="/assets/hero-tower-static.png"
                alt="XION Endoscopy Tower"
                className="absolute inset-0 w-full h-full object-contain"
                style={TOWER_SHADOW}
                data-testid="hero-tower-static-mobile"
              />
              <div className="tower-glow" aria-hidden />
              <div className="tower-spotlight" style={TOWER_MASK_STYLE} aria-hidden />
              <div
                className="tower-reticle"
                style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
                aria-hidden
              >
                <span className="tr-ring" style={{ borderColor: accent, boxShadow: `0 0 18px ${accent}55` }} />
                <span className="tr-dot" style={{ background: accent, boxShadow: `0 0 14px ${accent}` }} />
              </div>
            </div>
            {/* active-stage badge */}
            <div className="absolute top-3 left-3 right-3 flex items-center justify-between pointer-events-none">
              <span className="terminal-label text-[#00FFD1] text-[10px] bg-black/60 px-2 py-1 rounded">
                // {stage.tag}
              </span>
              <span className="font-mono text-[10px] text-white/60 bg-black/60 px-2 py-1 rounded">
                {stage.id}/{STAGES.length}
              </span>
            </div>
            {/* bottom fade so scrolling cards disappear cleanly under the tower */}
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#0c0c0c] to-transparent pointer-events-none" />
          </div>

          {/* Stage cards — each drives the spotlight as it crosses the active line */}
          <div className="relative z-10 px-5 pb-20 pt-6 space-y-6">
            {STAGES.map((s, idx) => (
              <div
                key={s.id}
                ref={(el) => (cardRefs.current[idx] = el)}
                data-stage={idx}
                className={`hud-card corner-brackets rounded-lg p-5 transition-all duration-300 ${
                  idx === stageIndex ? 'opacity-100 border-[#00FFD1]/60 shadow-[0_0_30px_-8px_rgba(0,255,209,0.5)]' : 'opacity-60'
                }`}
                data-testid={`hero-stage-card-${s.id}`}
              >
                <div className="terminal-label text-[#00FFD1] text-xs mb-2">// {s.tag} · {s.id}/{STAGES.length}</div>
                <h3 className="text-2xl font-bold text-[#00FFD1] mb-1">{s.title}</h3>
                <p className="text-white/80 text-sm mb-1">{s.subtitle}</p>
                <p className="text-white/55 text-sm leading-relaxed mb-3">{s.description}</p>
                <Link to={s.cta.link} className="btn-iridescent inline-flex text-sm" data-testid={`hero-stage-card-cta-${s.id}`}>
                  {s.cta.text}
                  <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default MultiPanelHero;
